import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { fetchOfflinePointers } from '../../utils/routing/offlineData';
import { saveRoutingArea, routingFilesFor } from '../../utils/routing/engine';
import { DEFAULT_RADIUS_KM, areaAround, readBasemap, rememberArea } from '../../utils/offline/basemap';
import { isQuota } from '../../utils/offline/byteStore';

/*
 * "Save this area for offline use": the base map within DEFAULT_RADIUS_KM of
 * the target, zoom 8 to 15, and the road-and-trail files that cover it, so
 * the map and the way to the target both work with no signal.
 *
 * Asks first: it estimates the download (the archive's own average tile size
 * times the tiles, plus the routing files' sizes from their manifest) and
 * waits for a yes. Then it shows progress, can be cancelled (what arrived
 * stays), asks the browser to keep the data (navigator.storage.persist), and
 * says plainly when the device is full.
 *
 * `loadVector` is the lazy import of utils/offline/vector.js, injectable for
 * the tests.
 */
const MB = 1_000_000;

export default function SaveAreaButton({ center, label = '', compact = false, radiusKm = DEFAULT_RADIUS_KM,
  loadVector = () => import('../../utils/offline/vector'), testid = 'save-area' }) {
  const { t } = useTranslation('offline');
  const [phase, setPhase] = useState('idle'); // idle | estimating | confirm | saving | done | failed | unavailable
  const [plan, setPlan] = useState(null);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [error, setError] = useState('');
  const abort = useRef(null);
  useEffect(() => () => abort.current?.abort(), []);

  if (!center || !Number.isFinite(Number(center.lat)) || !Number.isFinite(Number(center.lng))) return null;
  const bbox = areaAround({ lat: Number(center.lat), lng: Number(center.lng) }, radiusKm);

  const estimate = async () => {
    setPhase('estimating');
    setError('');
    try {
      const pointers = await fetchOfflinePointers();
      const out = { bbox, bytes: 0, tiles: 0, basemap: null, routing: null };
      if (pointers?.basemap?.pointerUrl) {
        try {
          const info = await readBasemap(pointers.basemap.pointerUrl);
          const vector = await loadVector();
          const est = await vector.estimateArea(info, bbox);
          out.basemap = info;
          out.tiles = est.tiles;
          out.bytes += est.bytes;
        } catch (e) { /* no base map: the routing files alone */ }
      }
      if (pointers?.routing?.pointerUrl) {
        try {
          const files = await routingFilesFor({ pointerUrl: pointers.routing.pointerUrl, bbox });
          out.routing = pointers.routing.pointerUrl;
          out.bytes += files.bytes;
        } catch (e) { /* no routing data: the map alone */ }
      }
      if (!out.basemap && !out.routing) {
        setPhase('unavailable');
        return;
      }
      setPlan(out);
      setPhase('confirm');
    } catch (e) {
      setError(String(e?.message || e));
      setPhase('failed');
    }
  };

  const save = async () => {
    setPhase('saving');
    const controller = new AbortController();
    abort.current = controller;
    try { await navigator.storage?.persist?.(); } catch (e) { /* the browser decides */ }
    try {
      let tiles = 0;
      if (plan.routing) await saveRoutingArea({ pointerUrl: plan.routing, bbox: plan.bbox });
      if (plan.basemap) {
        const vector = await loadVector();
        const out = await vector.saveArea(plan.basemap, plan.bbox, {
          signal: controller.signal, onProgress: (done, total) => setProgress({ done, total }),
        });
        tiles = out.done;
      }
      if (controller.signal.aborted) {
        setPhase('idle');
        return;
      }
      rememberArea({ id: `${plan.bbox.map((n) => n.toFixed(3)).join(',')}`, label, bbox: plan.bbox,
        savedAt: new Date().toISOString(), tiles, bytes: plan.bytes, version: plan.basemap?.version || null });
      setPhase('done');
    } catch (e) {
      setError(isQuota(e) ? t('quota') : String(e?.message || e));
      setPhase('failed');
    } finally {
      abort.current = null;
    }
  };

  const mb = plan ? Math.max(1, Math.round(plan.bytes / MB)) : 0;
  const button = compact ? fs.compact : fs.button;
  return (
    <div data-testid={testid} data-phase={phase} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      {(phase === 'idle' || phase === 'done' || phase === 'failed' || phase === 'unavailable') && (
        <button type="button" onClick={estimate} title={t('tip.save')} data-testid={`${testid}-start`} style={button}>
          <span aria-hidden="true">⬇ </span>{compact ? t('saveShort') : t('save')}
        </button>
      )}
      {phase === 'estimating' && <span role="status" style={fs.note}>{t('estimating')}</span>}
      {phase === 'confirm' && (
        <span role="group" style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span style={fs.note} data-testid={`${testid}-estimate`}>{t('confirm', { mb, km: radiusKm })}</span>
          <span style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            <button type="button" onClick={save} data-testid={`${testid}-confirm`} style={button}>{t('download')}</button>
            <button type="button" onClick={() => setPhase('idle')} style={button}>{t('cancel')}</button>
          </span>
        </span>
      )}
      {phase === 'saving' && (
        <span style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
          <span role="status" style={fs.note} data-testid={`${testid}-progress`}>
            {t('saving', { done: progress.done, total: progress.total || plan?.tiles || 0 })}
          </span>
          <button type="button" onClick={() => abort.current?.abort()} title={t('tip.cancel')} style={button}
            data-testid={`${testid}-cancel`}>{t('cancel')}</button>
        </span>
      )}
      {phase === 'done' && <span role="status" style={fs.note} data-testid={`${testid}-done`}>{t('done', { mb })}</span>}
      {phase === 'unavailable' && <span role="status" style={fs.note}>{t('unavailable')}</span>}
      {phase === 'failed' && <span role="alert" style={{ ...fs.note, color: 'var(--c-danger-fg, #ffb4a9)' }}>
        {t('failed', { error })}</span>}
    </div>
  );
}

const fs = {
  button: {
    minHeight: 36, padding: '0 12px', borderRadius: 6, border: '1px solid var(--c-border, rgba(255,255,255,.5))',
    background: 'var(--c-surface, rgba(0,0,0,.4))', color: 'inherit', font: 'inherit', fontSize: 14, cursor: 'pointer',
  },
  compact: {
    minHeight: 32, padding: '0 8px', borderRadius: 6, border: '1px solid rgba(255,255,255,.5)', background: 'transparent',
    color: 'inherit', font: 'inherit', fontSize: 12, cursor: 'pointer', width: '100%',
  },
  note: { fontSize: 12, lineHeight: 1.35 },
};
